/**
 * Flow API Service
 * Connects flow visualization to the canonical /flow/runs backend contract.
 */

import { useQuery } from "@tanstack/react-query";
import {
  socketFedRefetchInterval,
  socketFedStaleTime,
} from "@/features/websockets/utils/stream-health";
import {
  getRunSnapshot,
  listFlowRuns,
  type RunListItem,
  type RunSnapshot as ContractRunSnapshot,
} from "./run-contract.service";
import type {
  FlowNodeData,
  FlowNodeStatus,
  FlowRun,
  FlowRunSummary,
  MtfTimeframeData,
} from "../types/flow.types";

function mapSnapshotStatus(
  status: ContractRunSnapshot["nodes"][number]["status"],
): FlowNodeStatus {
  return status;
}

function snapshotToFlowRun(snapshot: ContractRunSnapshot): FlowRun {
  const adaptMtfTimeframes = (
    timeframes: ContractRunSnapshot["nodes"][number]["mtf_timeframes"],
  ): MtfTimeframeData[] | undefined => {
    if (!timeframes?.length) {
      return undefined;
    }

    return timeframes
      .filter(
        (tf): tf is typeof tf & { timeframe: MtfTimeframeData["timeframe"] } =>
          tf.timeframe === "1m" ||
          tf.timeframe === "5m" ||
          tf.timeframe === "15m" ||
          tf.timeframe === "1h" ||
          tf.timeframe === "4h",
      )
      .map((tf) => ({
        timeframe: tf.timeframe,
        screenshot_url: tf.screenshot_url ?? undefined,
        indicators: tf.indicators as MtfTimeframeData["indicators"],
        price_vs_vwap: tf.price_vs_vwap ?? "at",
        price_vs_sr: tf.price_vs_sr ?? "between_levels",
        trend_direction: tf.trend_direction,
        visual_score: tf.visual_score,
        captured_at: tf.captured_at,
      }));
  };

  const nodes: FlowNodeData[] = snapshot.nodes.map((node) => ({
    id: node.node_id,
    name: node.name,
    status: mapSnapshotStatus(node.status),
    duration: node.duration_ms ?? 0,
    current: node.node_id === snapshot.current_node_id,
    message: node.message || undefined,
    data: node.data || undefined,
    agent: node.agent_name
      ? {
          name: node.agent_name,
          avatarUrl: node.agent_avatar || undefined,
        }
      : undefined,
    agentKey: node.agent_key || undefined,
    mtfTimeframes: adaptMtfTimeframes(node.mtf_timeframes),
    overallScore: node.overall_score,
    overallDirection: node.overall_direction,
    conflictingTimeframes: node.conflicting_timeframes,
  }));

  return {
    id: snapshot.run_id,
    mode: "LIVE",
    nodes,
    duration: snapshot.nodes.reduce(
      (total, node) => total + (node.duration_ms ?? 0),
      0,
    ),
    timestamp: snapshot.started_at,
  };
}

function summaryStatus(
  run: RunListItem,
): "success" | "failed" | "running" | "no-trade" {
  if (run.status === "completed") {
    return "success";
  }
  if (run.status === "rejected") {
    // A rejected run ended with a NO-TRADE verdict, not a failure.
    return "no-trade";
  }
  if (run.status === "failed" || run.status === "cancelled") {
    return "failed";
  }
  return "running";
}

function runToSummary(run: RunListItem): FlowRunSummary {
  return {
    id: run.run_id,
    timestamp: run.started_at,
    status: summaryStatus(run),
    error: run.error,
    duration: run.execution_time_ms || 0,
  };
}

/**
 * Flow API Service
 */
export class FlowAPIService {
  private static instance: FlowAPIService;

  private constructor() {}

  static getInstance(): FlowAPIService {
    if (!FlowAPIService.instance) {
      FlowAPIService.instance = new FlowAPIService();
    }
    return FlowAPIService.instance;
  }

  /**
   * Get flow run history
   */
  async getFlowHistory(): Promise<FlowRunSummary[]> {
    const runs = await listFlowRuns();
    return runs.map(runToSummary);
  }

  /**
   * Get flow run details by ID
   */
  async getFlowRun(instanceId: string): Promise<FlowRun | null> {
    const snapshot = await getRunSnapshot(instanceId);
    if (!snapshot) return null;
    return snapshotToFlowRun(snapshot);
  }

  /**
   * Get current/active flow run
   */
  async getCurrentFlowRun(): Promise<FlowRun | null> {
    const runs = await listFlowRuns();
    const activeRun = runs.find(
      (run) => run.status === "running" || run.status === "pending",
    );
    if (!activeRun) return null;
    const snapshot = await getRunSnapshot(activeRun.run_id);
    return snapshotToFlowRun(snapshot);
  }
}

// Singleton instance
export const flowAPIService = FlowAPIService.getInstance();

// ==============================================================================
// React Query Hooks
// ==============================================================================

// Query keys for flow data
export const flowQueryKeys = {
  all: ["flow"] as const,
  history: () => [...flowQueryKeys.all, "history"] as const,
  run: (id: string) => [...flowQueryKeys.all, "run", id] as const,
  current: () => [...flowQueryKeys.all, "current"] as const,
};

/**
 * Hook to fetch flow run history
 * Following Pattern 3 from frontend-websocket skill: WebSocket updates React Query cache
 */
export function useFlowHistory() {
  return useQuery({
    queryKey: flowQueryKeys.history(),
    queryFn: () => flowAPIService.getFlowHistory(),
    // History data doesn't change frequently — no live-truth claim; declines
    // to poll (frontend-conventions §Socket-fed money surfaces, exceptions).
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a specific flow run
 */
export function useFlowRun(instanceId: string | null) {
  return useQuery({
    queryKey: flowQueryKeys.run(instanceId || ""),
    queryFn: () => (instanceId ? flowAPIService.getFlowRun(instanceId) : null),
    enabled: !!instanceId,
    // Socket-fed live surface: the socket owns freshness, the finite safety
    // poll owns truth (frontend-conventions §Socket-fed money surfaces).
    staleTime: socketFedStaleTime,
    refetchInterval: socketFedRefetchInterval,
  });
}

/**
 * Hook to fetch current active flow run
 */
export function useCurrentFlowRun() {
  return useQuery({
    queryKey: flowQueryKeys.current(),
    queryFn: () => flowAPIService.getCurrentFlowRun(),
    // Socket-fed live surface: the socket owns freshness, the finite safety
    // poll owns truth (frontend-conventions §Socket-fed money surfaces).
    staleTime: socketFedStaleTime,
    refetchInterval: socketFedRefetchInterval,
  });
}

/**
 * Hook to get flow data with automatic switching between live and replay
 */
export function useFlowData(options: {
  mode: "LIVE" | "REPLAY";
  selectedRunId?: string | null;
}) {
  const { mode, selectedRunId } = options;

  // Use current run for live mode, selected run for replay
  const currentQuery = useCurrentFlowRun();
  const selectedQuery = useFlowRun(selectedRunId || null);

  if (mode === "LIVE") {
    return currentQuery;
  }

  return selectedQuery;
}
